/**
 * @file: 商品物理尺寸计算, 单位厘米
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../cubeTree/index';
import FrontCubeData from '../../frontCubeData/index';
import Utils from '../../utils/util';
import Config from '../../config';
import { naiSize } from '../layer/naiSize';

interface SizeMap {
    mainGoods: PhysicsSize,
    xGoods: PhysicsSize,
    yGoods: PhysicsSize,
    naiSizeInfo: PhysicsSize,
    subGoods: PhysicsSize
}

const singleGoodsDefaultWidth: number = Config.get({
    moduleName: 'goods',
    key: 'singleGoodsDefaultWidth'
});
const singleGoodsDefaultHeight: number = Config.get({
    moduleName: 'goods',
    key: 'singleGoodsDefaultHeight'
});

// 不包含字码商品, 包含间距posWidth
function centerGoodsWithSpaceSizeByCubeId(cubeId: string): PhysicsSize {
    let cube: CubeNode = CubeTree.findByCubeId(cubeId);
    let surfaceWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'surfaceWidth');
    let posWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'posWidth');
    let result: PhysicsSize = onlyCenterGoodsSize(cube);
    let posWidthSum: number = Utils.math(surfaceWidth - 1, posWidth, '*');

    result.width = Utils.math(result.width, posWidthSum, '+');

    return result;
}

// 不包含字码商品, 不包含间距posWidth
function onlyCenterGoodsSizeByCubeId(cubeId: string): PhysicsSize {
    let cube: CubeNode = CubeTree.findByCubeId(cubeId);

    return onlyCenterGoodsSize(cube);
}
function onlyCenterGoodsSize(cube: CubeNode): PhysicsSize {
    let parentId: CubeId = CubeTree.findParentIdByCubeId(cube.id);
    let sizeMap: SizeMap = makeSizeMap(cube);
    let result: PhysicsSize = {
        width: 0,
        height: 0
    };

    if (CubeTree.isBeam(parentId)) {
        // 求宽
        let maxWidth: number = Math.max(sizeMap.yGoods.width, sizeMap.mainGoods.width);
        let widthSum: number = Utils.math(maxWidth, sizeMap.xGoods.width, '+');
        result.width = Math.max(widthSum, sizeMap.naiSizeInfo.width);

        // 求高
        let heightSum: number = Utils.math(sizeMap.yGoods.height, sizeMap.mainGoods.height, '+');
        let heightMax: number = Math.max(sizeMap.xGoods.height, heightSum);
        result.height = Utils.math(heightMax, sizeMap.naiSizeInfo.height, '+');
    }
    else {
        // 求宽
        let maxWidth: number = Math.max(sizeMap.yGoods.width, sizeMap.mainGoods.width);
        let widthSum: number = Utils.math(maxWidth, sizeMap.xGoods.width, '+');
        result.width = Math.max(widthSum, sizeMap.naiSizeInfo.width);

        // 求高
        let heightSum: number = Utils.math(sizeMap.yGoods.height, sizeMap.mainGoods.height, '+');
        let maxHeight: number = Math.max(sizeMap.xGoods.height, heightSum);
        result.height = Utils.math(maxHeight, sizeMap.naiSizeInfo.height, '+');
    }

    return result;
}

// 包含子码商品，包含posWidth
function containSubGoodsWithPosWidthSizeByCubeId(cubeId: string): PhysicsSize {
    let cube: CubeNode = CubeTree.findByCubeId(cubeId);
    let surfaceWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'surfaceWidth');
    let posWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'posWidth');
    let result: PhysicsSize = containSubGoodsSize(cube);

    if (posWidth > 0) {
        let realyPosWidth: number = Utils.math(posWidth, diffWidthMainAndSub(cubeId), '-');
        let totalPosWidth: number = Utils.math((surfaceWidth - 1), realyPosWidth, '*');

        result.width = Utils.math(totalPosWidth, result.width, '+');
    }

    return result;
}

// 包含子码商品，不包含posWidth
function containSubGoodsSizeByCubeId(cubeId: string): PhysicsSize {
    let cube: CubeNode = CubeTree.findByCubeId(cubeId);

    return containSubGoodsSize(cube);
}
function containSubGoodsSize(cube: CubeNode): PhysicsSize {
    let parentId: CubeId = CubeTree.findParentIdByCubeId(cube.id);
    let sizeMap: SizeMap = makeSizeMap(cube);
    let result: PhysicsSize = {
        width: 0,
        height: 0
    };

    let maxColWidth: PhysicsMeasurement = Math.max(sizeMap.yGoods.width, sizeMap.mainGoods.width, sizeMap.subGoods.width);
    let totalWidth: PhysicsMeasurement = Utils.math(maxColWidth, sizeMap.xGoods.width, '+');

    result.width = Math.max(totalWidth, sizeMap.naiSizeInfo.width);

    if (CubeTree.isBeam(parentId)) {
        let centerHeight: number = Utils.math(sizeMap.yGoods.height, sizeMap.mainGoods.height, '+');
        let maxHeight: number = Math.max(sizeMap.xGoods.height, centerHeight, sizeMap.subGoods.height);

        result.height = Utils.math(maxHeight, sizeMap.naiSizeInfo.height, '+');
    }
    else {
        let heightSum1: number = Utils.math(sizeMap.yGoods.height, sizeMap.mainGoods.height, '+');
        let heightSum2: number = Utils.math(heightSum1, sizeMap.subGoods.height, '+');
        let max: number = Math.max(sizeMap.xGoods.height, heightSum2);

        result.height = Utils.math(max, sizeMap.naiSizeInfo.height, '+');
    }

    return result;
}

function makeSizeMap(cube: CubeNode): SizeMap {
    let parentId: CubeId = CubeTree.findParentIdByCubeId(cube.id);
    let result: PhysicsSize = {
        width: 0,
        height: 0
    };
    let naiSizeInfo: PhysicsSize = {
        width: 0,
        height: 0
    };

    if (CubeTree.isBeam(parentId)) {
        naiSizeInfo = naiSize(cube);
    }

    let mainGoods: PhysicsSize = centerGoodsSize(cube);
    let subGoods: PhysicsSize = subGoodsSize(cube);
    let xGoods: PhysicsSize = xGoodsSize(cube);
    let yGoods: PhysicsSize = yGoodsSize(cube);

    return {
        mainGoods,
        xGoods,
        yGoods,
        naiSizeInfo,
        subGoods
    }
}

// 主商品尺寸，包含多面宽
function centerGoodsSize(cube: CubeNode): PhysicsSize {
    let surfaceWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'surfaceWidth');
    let showWareWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showWareWidth') || singleGoodsDefaultWidth;
    let surfaceHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'surfaceHeight');
    let showWareHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showWareHeight') || singleGoodsDefaultHeight;
    let subSkuList: Array<SkuData> = FrontCubeData.getValue(cube.id, 'subSkuList');

    if (subSkuList.length > 1) {
        var sku: SkuData = subSkuList[0];

        surfaceWidth = sku.surfaceWidth;
        surfaceHeight = sku.surfaceHeight;
    }

    return {
        width: Utils.math(surfaceWidth, showWareWidth, '*'),
        height: Utils.math(surfaceHeight, showWareHeight, '*')
    };
}

// 主商品尺寸，不包含多面宽
function centerGoodsSizeOneSurface(cubeId: string): PhysicsSize {
    let cube: CubeNode = CubeTree.findByCubeId(cubeId);
    let showWareWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showWareWidth') || singleGoodsDefaultWidth;
    let showWareHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showWareHeight') || singleGoodsDefaultHeight;

    return {
        width: showWareWidth,
        height: showWareHeight
    };
}

// x轴叠放商品尺寸
function xGoodsSize(cube: CubeNode): PhysicsSize {
    let xPileSurfaceWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'xPileSurfaceWidth');
    let showXPileWareWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showXPileWareWidth') || singleGoodsDefaultWidth;
    let xPileSurfaceHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'xPileSurfaceHeight');
    let showXPileWareHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showXPileWareHeight') || singleGoodsDefaultHeight;

    return {
        width: Utils.math(xPileSurfaceWidth, showXPileWareWidth, '*'),
        height: Utils.math(xPileSurfaceHeight, showXPileWareHeight, '*')
    }
}

// y轴叠放商品尺寸
function yGoodsSize(cube: CubeNode): PhysicsSize {
    let yPileSurfaceWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'yPileSurfaceWidth');
    let showYPileWareWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showYPileWareWidth') || singleGoodsDefaultWidth;
    let yPileSurfaceHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'yPileSurfaceHeight');
    let showYPileWareHeight: PhysicsMeasurement = FrontCubeData.getNumberValue(cube.id, 'showYPileWareHeight') || singleGoodsDefaultHeight;

    return {
        width: Utils.math(yPileSurfaceWidth, showYPileWareWidth, '*'),
        height: Utils.math(yPileSurfaceHeight, showYPileWareHeight, '*')
    }
}

// 字码商品尺寸，包含面宽
function subGoodsSize(cube: CubeNode): PhysicsSize {
    let parentId: CubeId = CubeTree.findParentIdByCubeId(cube.id);
    let subSkuList: Array<SkuData> = FrontCubeData.getValue(cube.id, 'subSkuList');
    let result: PhysicsSize = {
        width: 0,
        height: 0
    };

    for (let i: number = 1; i < subSkuList.length; i++) {
        let sku: SkuData = subSkuList[i];

        if (CubeTree.isBeam(parentId)) {
            // 横杆
            result.height = Math.max(result.height, sku.height);
        }
        else {
            result.height = Utils.math(sku.height, result.height, '+');
        }

        let widthSum: number = Utils.math(sku.width, sku.surfaceWidth, '*');

        result.width = Math.max(result.width, widthSum);
    }

    return result;
}

// 字码商品尺寸，不计算面宽
function subGoodsSizeNoSurface(cube: CubeNode): PhysicsSize {
    let parentId: CubeId = CubeTree.findParentIdByCubeId(cube.id);
    let subSkuList: Array<SkuData> = FrontCubeData.getValue(cube.id, 'subSkuList');
    let result: PhysicsSize = {
        width: 0,
        height: 0
    };

    for (let i: number = 1; i < subSkuList.length; i++) {
        let sku: SkuData = subSkuList[i];

        if (CubeTree.isBeam(parentId)) {
            // 横杆
            result.height = Math.max(result.height, sku.height);
        }
        else {
            result.height = Utils.math(sku.height, result.height, '+');
        }

        result.width = Math.max(result.width, sku.width);
    }

    return result;
}

// 字码超出主码的宽度
function diffWidthMainAndSub(cubeId: string): PhysicsMeasurement {
    let result: PhysicsMeasurement = 0;
    let cube: CubeNode = CubeTree.findByCubeId(cubeId);
    let sub: PhysicsSize = subGoodsSizeNoSurface(cube);
    let main: PhysicsSize = centerGoodsSizeOneSurface(cubeId);

    if (sub.width > main.width) {
        result = Utils.math(sub.width, main.width, '-');
    }

    return result;
}

export {
    centerGoodsSizeOneSurface,
    centerGoodsWithSpaceSizeByCubeId,
    onlyCenterGoodsSizeByCubeId,
    onlyCenterGoodsSize,
    containSubGoodsWithPosWidthSizeByCubeId,
    containSubGoodsSizeByCubeId,
    containSubGoodsSize,
    diffWidthMainAndSub
};
